import cx from 'classnames';
import { t } from 'i18next';
import _ from 'lodash';
import Numeral from 'numeral';
import { useState } from 'react';
import { Link, TrackedLink } from '../../../../../../../shared/frontend/components/link';
import { ComplexTooltip, SimpleTooltip } from '../../../../../../../shared/frontend/components/presentational/tooltip';
import { trackLinkingToVideos } from '../../../../../../../shared/frontend/libs/utils';
import { TVideoTrackType } from '../../../../../../../shared/common/videoUtils';
import WowIcon from '../../../../../../../shared/frontend/scripts/wow/components/WowIcon';
import { WowheadLink } from '../../../../../../../shared/frontend/scripts/wow/components/WowheadLink';
import { getPathToRun, namedRoutes, resolveRoutePath } from '../../../bundles/routes';
import LoadableKeystoneRunContent from '../../container/loadableKeystoneRunContent';
import { GuildRank } from '../guildRank';
import { MythicPlusLevel } from '../mythicPlusLevel';
import { TimeStyling } from './TimeStyling';
import { KeystoneRunTimeInfo } from './KeystoneRunTimeInfo';
import { KeystoneGroupMembers } from './KeystoneGroupMembers';
import { TournamentRegionForRun, getPlatoonAssignmentUrl, openPlatoonAssignmentPopup } from './helpers';

export function KeystoneRunRow(props: any) {
  const group = props.group;
  const videos = group.run.videos;
  const cols = props.cols;
  const isOpen = props.isOpen;
  const eventNamespace = props.eventNamespace;
  const [areDetailsOpened, setDetailsOpened] = useState(isOpen);

  return (
    <>
      <tr
        className={`mythic-plus-rankings--row faction--${group.run.faction}`}
        {...(cols.showRun
          ? {
              style: { cursor: 'pointer' },
              onClick: (domEvent: any) => {
                if (!domEvent.metaKey) {
                  domEvent.preventDefault();
                  RIO_TRACK_EVENT('event', 'select_content', {
                    content_type: areDetailsOpened ? 'lb_mplus_run_close' : 'lb_mplus_run_expand',
                    content_id: `${group.run.season}/${group.run.keystone_run_id}`
                  });
                  setDetailsOpened((p: any) => !p);
                }
              }
            }
          : {})}
      >
        {cols.showRun && (
          <td data-label="Show Details" className="slds-text-align--center">
            <i className={cx('fa fa-fw', areDetailsOpened ? 'fa-caret-down' : 'fa-caret-right')} />
          </td>
        )}
        {cols.runLink && (
          <td data-label="Run" className="slds-text-align--center">
            <Link to={getPathToRun(group.run)}>
              <i className="fa fa-fw fa-link" />
            </Link>
          </td>
        )}
        {cols.rank && (
          <td data-label="Rank" className="slds-text-align--center">
            <Link to={getPathToRun(group.run)}>
              <GuildRank rank={group.rank} />
            </Link>
          </td>
        )}
        {cols.tournamentRegion && (
          <td className="slds-text-align--center">
            <TournamentRegionForRun run={group.run} />
          </td>
        )}
        {cols.dungeon && (
          <td className="slds-text-align--center">
            <SimpleTooltip
              placement="top"
              tooltip={
                <span>
                  <p className="slds-text-align--center">{t(`raids:${group.run.dungeon.slug}.name`)}</p>
                </span>
              }
            >
              <span>
                <Link to={getPathToRun(group.run)} title="Link to Run" className="rio-realm-link nohover">
                  {t(`raids:${group.run.dungeon.slug}.acronym`)}
                </Link>
              </span>
            </SimpleTooltip>
          </td>
        )}
        {cols.level && (
          <td data-label="Mythic Level">
            <SimpleTooltip
              placement="top"
              tooltip={
                <span>
                  <span className="slds-text-body--x-small slds-text-align--left text-white">
                    {group.run.num_chests !== 0 ? <span>Keystone Upgraded +{group.run.num_chests}</span> : null}
                    {group.run.num_chests === 0 ? <span style={{ color: '#aaa' }}>Keystone Depleted</span> : null}
                  </span>
                </span>
              }
            >
              <span>
                <Link to={getPathToRun(group.run)} className="rio-realm-link nohover">
                  <MythicPlusLevel level={group.run.mythic_level} numChests={group.run.num_chests} />
                </Link>
              </span>
            </SimpleTooltip>
          </td>
        )}
        {cols.time && (
          <td className="slds-text-align--center">
            <ComplexTooltip placement="top" tooltip={<KeystoneRunTimeInfo run={group.run} />}>
              <span>
                <Link to={getPathToRun(group.run)} className="rio-realm-link">
                  <TimeStyling
                    timeMs={group.run.clear_time_ms}
                    precision={cols.timePrecision}
                    includeHours={!cols.timeSkipHours}
                  />
                </Link>
              </span>
            </ComplexTooltip>
          </td>
        )}
        {cols.affixes && (
          <td>
            {_.range(group.run.num_modifiers_active).map((index: number) => (
              <WowheadLink
                key={index}
                type="affix"
                id={group.run.weekly_modifiers[index].id}
                rel={`affix=${group.run.weekly_modifiers[index].id}`}
                className="rio-spaced-element--small"
              >
                <WowIcon icon={group.run.weekly_modifiers[index].icon} size="x-small" />
              </WowheadLink>
            ))}
          </td>
        )}
        {cols.tank && (
          <td className="slds-text-align--left">
            <KeystoneGroupMembers
              allowWrap={props.allowGroupMembersWrap}
              eventNamespace={eventNamespace}
              teamDetailsRoute={props.teamDetailsRoute}
              run={group.run}
              role="tank"
            />
          </td>
        )}
        {cols.healer && (
          <td className="slds-text-align--left">
            <KeystoneGroupMembers
              allowWrap={props.allowGroupMembersWrap}
              eventNamespace={eventNamespace}
              teamDetailsRoute={props.teamDetailsRoute}
              run={group.run}
              role="healer"
            />
          </td>
        )}
        {cols.dps && (
          <td className="slds-text-align--left">
            <KeystoneGroupMembers
              allowWrap={props.allowGroupMembersWrap}
              eventNamespace={eventNamespace}
              teamDetailsRoute={props.teamDetailsRoute}
              run={group.run}
              role="dps"
            />
          </td>
        )}
        {cols.group && (
          <td className="slds-text-align--left">
            <KeystoneGroupMembers
              teamDetailsRoute={props.teamDetailsRoute}
              run={group.run}
              role="tank"
              className="d-inline-block"
            />
            <KeystoneGroupMembers
              teamDetailsRoute={props.teamDetailsRoute}
              run={group.run}
              role="healer"
              className="d-inline-block"
            />
            <KeystoneGroupMembers
              teamDetailsRoute={props.teamDetailsRoute}
              run={group.run}
              role="dps"
              className="d-inline-block"
            />
          </td>
        )}
        {cols.score && (
          <td className="slds-text-align--right">
            <Link
              to={getPathToRun(group.run)}
              className={cx({ 'rio-realm-link': true, 'text-muted': group.run.num_chests === 0 })}
            >
              {Numeral(group.score).format('0,0.0')}
            </Link>
          </td>
        )}
        {cols.faction && (
          <td>
            <div
              title={`${_.capitalize(group.run.faction)} Group`}
              className={`slds-m-right--x-small icon-faction-${group.run.faction}`}
            />
          </td>
        )}
        {!props.isEmbedded && cols.video && (
          <td className="slds-text-align--center">
            {videos?.length > 0 ? (
              <ComplexTooltip placement="top" tooltip={<span>VOD Available</span>}>
                <Link
                  className="no-underline-hover rio-realm-link rio-simple-button"
                  to={resolveRoutePath(namedRoutes.MythicKeystoneRun, {
                    season: group.run.season,
                    id: group.run.keystone_run_id
                  })}
                  onClick={() => trackLinkingToVideos(TVideoTrackType.MPlus)}
                >
                  <i className="fa-solid fa-video-camera"></i>
                </Link>
              </ComplexTooltip>
            ) : (
              ''
            )}
          </td>
        )}
        {!props.isEmbedded &&
          cols.route &&
          (group.run.privacy?.route ? (
            <td className="slds-text-align--center">
              <div>
                <ComplexTooltip placement="top" tooltip={<span>Route Available</span>}>
                  <i className="fa fa-fw fa-map-location-dot" />
                </ComplexTooltip>
              </div>
            </td>
          ) : (
            <td>
              <div />
            </td>
          ))}
        {!props.isEmbedded &&
          cols.replay &&
          (group.run.privacy?.replay ? (
            <td className="slds-text-align--center">
              <div>
                <ComplexTooltip placement="top" tooltip={<span>Replay Available</span>}>
                  <i className="fa fa-fw fa-route" />
                </ComplexTooltip>
              </div>
            </td>
          ) : (
            <td>
              <div />
            </td>
          ))}
        {cols.platoonAssigned && (
          <td>
            {group.run.keystone_platoon_id ? (
              <span />
            ) : (
              <ComplexTooltip
                placement="top"
                tooltip={<span className="slds-text-align--center">{t('mdi:shared.team-not-set')}</span>}
              >
                {props.isMdiManager ? (
                  <TrackedLink
                    href={getPlatoonAssignmentUrl(
                      group.run.keystone_team_id,
                      group.run.roster.map((r: any) => r.character.name).join(', '),
                      props.platoonNamespace,
                      props.bracketId
                    )}
                    target="_blank"
                    onClick={openPlatoonAssignmentPopup.bind(
                      null,
                      group.run.keystone_team_id,
                      group.run.roster.map((r: any) => r.character.name).join(', '),
                      props.platoonNamespace,
                      props.bracketId
                    )}
                    title="Assign Platoon"
                    className={`platoon-comp-icon-${group.run.keystone_team_id}`}
                  >
                    <span className="text-muted">
                      <i className="fa fa-exclamation-triangle" /> &nbsp;
                    </span>
                  </TrackedLink>
                ) : (
                  <span className="text-muted">
                    <i className="fa fa-exclamation-triangle" /> &nbsp;
                  </span>
                )}
              </ComplexTooltip>
            )}
          </td>
        )}
      </tr>
      {areDetailsOpened && (
        <tr>
          <td colSpan={100} data-label="Group Composition" style={{ backgroundColor: 'rgb(31, 31, 31)' }}>
            <LoadableKeystoneRunContent
              season={group.run.season}
              keystoneRunId={group.run.keystone_run_id}
              expandableCharacterRow={props.expandableCharacterRow}
              showMythicPlusShare={true}
              showMythicPlusVideos={true}
            />
          </td>
        </tr>
      )}
    </>
  );
}
